import React from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from '@tanstack/react-router'
import { logVersionInfo } from '@/utils/version'
import { logConfig } from '@/config/config'
import { info } from '@/utils/logger'
import { initSentry } from '@/config/sentry'
import { ErrorBoundary } from '@/components/app/common/error-boundary'
import { initRulerWasm } from '@/lib/boundaries/ruler-boundary'
import { initShapeWasm } from '@/lib/boundaries/shape-boundary'
import { initBoundingBoxWasm } from '@/lib/boundaries/bounding-box-boundary'
import { ThemeProvider } from '@/providers/theme'
import { QueryProvider } from '@/providers/query'
import { AuthProvider } from '@/providers/auth'
import { Toaster } from '@/components/ui/sonner'
import { router } from '@/router/routes'
import '@/index.css'
import { initClikArtWasm } from '@/lib/boundaries/clikart-boundary'

// Initialize Sentry for performance monitoring
initSentry()

// Log configuration on app startup
logConfig()
info('Application started successfully.')

// Initialize WASM early and wait for it
async function initializeApp() {
  try {
    // Initialize direct WASM for ruler computation
    await initRulerWasm()
    // Initialize direct WASM for shape computation
    await initShapeWasm()
    // Initialize direct WASM for bounding box computation
    await initBoundingBoxWasm()
    // Initialize direct WASM for clikArt computation
    await initClikArtWasm()
  } catch (error) {
    console.warn('WASM initialization failed, will use JS fallback:', error)
  }

  // Log version info once when app starts
  logVersionInfo()

  const root = document.getElementById('root')

  if (!root) {
    throw new Error('Root element not found')
  }

  createRoot(root).render(
    <React.StrictMode>
      <ErrorBoundary>
        <QueryProvider>
          <ThemeProvider defaultTheme="light" storageKey="wpc-ui-theme">
            <AuthProvider>
              <RouterProvider router={router} />
              <Toaster position="top-center" />
            </AuthProvider>
          </ThemeProvider>
        </QueryProvider>
      </ErrorBoundary>
    </React.StrictMode>
  )
}

// Start the app
initializeApp()
